import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { authService } from '@/features/auth/services/authService';
import { fetchKioskInfo } from '@/services/kioskInfoCache';

interface AutoLogoutConfig {
  timeout: number;
  warningThreshold: number;
  format: 'minutes' | 'seconds';
}

// Config by domain type (from /api/kiosk/info). Mobile: no idle timeout (logout only when token expires).
const TYPE_CONFIG: Record<string, AutoLogoutConfig> = {
  kiosk: { timeout: 5 * 60 * 1000, warningThreshold: 60 * 1000, format: 'minutes' },
};

// Local dev hostname overrides (synchronous, no API needed)
const DEV_HOST_CONFIG: Record<string, AutoLogoutConfig | null> = {
  'localhost': null, // disabled for local dev
  '127.0.0.1': { timeout: 5 * 60 * 1000, warningThreshold: 60 * 1000, format: 'minutes' },
};

export interface AutoLogoutState {
  timeRemaining: number; // in milliseconds
  isWarningVisible: boolean;
  resetTimer: () => void;
  format: 'minutes' | 'seconds'; // for the warning component to use
}

/**
 * Hook to automatically logout user after idle timeout.
 * Fetches /api/kiosk/info on mount to determine domain type:
 *   - kiosk  → 5 min idle timeout, 1 min warning
 *   - mobile → no idle timeout (session until token expires)
 *   - others → disabled
 */
export function useAutoLogout(): AutoLogoutState {
  const navigate = useNavigate();
  const location = useLocation();
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isActiveRef = useRef(false);
  const lastActivityRef = useRef<number>(Date.now());
  const [timeRemaining, setTimeRemaining] = useState<number>(0);
  const [isWarningVisible, setIsWarningVisible] = useState(false);

  // 'loading' = not yet resolved; null = disabled; AutoLogoutConfig = active
  const [config, setConfig] = useState<AutoLogoutConfig | null | 'loading'>('loading');

  // Fetch domain type once on mount
  useEffect(() => {
    const host = globalThis.location.hostname;

    // Check local dev overrides first (synchronous)
    if (host in DEV_HOST_CONFIG) {
      setConfig(DEV_HOST_CONFIG[host]);
      return;
    }

    void fetchKioskInfo().then((payload) => {
      const type = payload?.type ?? '';
      setConfig(TYPE_CONFIG[type] ?? null);
    });
  }, []);

  const resetTimer = () => {
    if (!isActiveRef.current) return;
    lastActivityRef.current = Date.now();
    setIsWarningVisible(false);
    setTimeRemaining(0);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  useEffect(() => {
    const clearTimers = () => {
      if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null; }
      if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
      isActiveRef.current = false;
      setIsWarningVisible(false);
      setTimeRemaining(0);
    };

    // Still loading or disabled — ensure timers are cleared
    if (config === 'loading' || !config) {
      clearTimers();
      return;
    }

    // Don't activate auto-logout on home page
    if (location.pathname === '/') {
      clearTimers();
      return;
    }

    isActiveRef.current = true;
    lastActivityRef.current = Date.now();

    const handleLogout = async () => {
      if (!isActiveRef.current) return;
      isActiveRef.current = false;
      if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null; }
      if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
      try { await authService.logout(); } catch { /* ignore */ }
      navigate('/', { replace: true });
    };

    const handleActivity = () => {
      if (isActiveRef.current) resetTimer();
    };

    const updateCountdown = () => {
      if (!isActiveRef.current) return;
      const elapsed = Date.now() - lastActivityRef.current;
      const remaining = Math.max(0, config.timeout - elapsed);
      setTimeRemaining(remaining);
      if (remaining <= config.warningThreshold && remaining > 0) {
        setIsWarningVisible(true);
      } else {
        setIsWarningVisible(false);
      }
      if (remaining <= 0) {
        handleLogout();
      }
    };

    intervalRef.current = setInterval(updateCountdown, 1000);
    timeoutRef.current = setTimeout(handleLogout, config.timeout);
    updateCountdown();

    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click', 'keypress'];
    events.forEach(event => globalThis.addEventListener(event, handleActivity, { passive: true }));

    return () => {
      if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null; }
      if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
      events.forEach(event => globalThis.removeEventListener(event, handleActivity));
      isActiveRef.current = false;
      setIsWarningVisible(false);
      setTimeRemaining(0);
    };
  }, [config, navigate, location.pathname]);

  return {
    timeRemaining,
    isWarningVisible,
    resetTimer,
    format: config && config !== 'loading' ? config.format : 'minutes',
  };
}
